import { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { LabelElement } from '@/types'
import { TextElementPreview } from '@/components/elements/TextElement'
import { BarcodeElementPreview } from '@/components/elements/BarcodeElement'
import { QRCodeElementPreview } from '@/components/elements/QRCodeElement'
import { DataMatrixElementPreview } from '@/components/elements/DataMatrixElement'
import { GS1DataMatrixElementPreview } from '@/components/elements/GS1DataMatrixElement'
import { GS1Code128ElementPreview } from '@/components/elements/GS1Code128Element'
import { ShapeElementPreview } from '@/components/elements/ShapeElement'
import { ImageElementComponent } from '@/components/elements/ImageElement'
import { useLabelStore } from '@/stores/labelStore'
import { useElementsStore } from '@/stores/elementsStore'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { Copy, Trash2, ArrowUpToLine, ArrowDownToLine, ChevronUp, ChevronDown } from 'lucide-react'

type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

interface CanvasElementProps {
  element: LabelElement
  isSelected: boolean
  zoom: number
  mmToPx: number
  onSelect: (e: React.MouseEvent) => void
  onDrag: (deltaX: number, deltaY: number, isDragEnd: boolean) => void
  onResize?: (width: number | undefined, height: number | undefined, diameter: number | undefined) => void
  onTextEdit?: (newContent: string) => void
}

// Helper to check if element is resizable and get its dimensions
function getElementDimensions(element: LabelElement): { width?: number; height?: number; diameter?: number } | null {
  switch (element.type) {
    case 'line':
    case 'rectangle':
    case 'image':
      return { width: element.width, height: element.height }
    case 'circle':
      return { diameter: element.diameter }
    default:
      return null
  }
}

// Helper to check which resize handles should be shown
function getResizeHandles(element: LabelElement): ResizeHandle[] {
  switch (element.type) {
    case 'line':
      // Line: horizontal resize only (east/west)
      return ['e', 'w']
    case 'rectangle':
    case 'image':
      // Rectangle/Image: all 8 handles
      return ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']
    case 'circle':
      // Circle: corner handles only (resize uniformly)
      return ['ne', 'nw', 'se', 'sw']
    default:
      return []
  }
}

export function CanvasElement({
  element,
  isSelected,
  zoom,
  mmToPx,
  onSelect,
  onDrag,
  onResize,
  onTextEdit,
}: CanvasElementProps) {
  const elementRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { label } = useLabelStore()
  const {
    duplicateElement,
    deleteElement,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
  } = useElementsStore()
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragStartRef = useRef({ x: 0, y: 0 })
  const totalDragRef = useRef({ x: 0, y: 0 })
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number; diameter: number }>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    diameter: 0,
  })

  // Check if this element supports inline editing
  const isTextEditable = element.type === 'text' && !element.isVariable

  const x = element.position.x * mmToPx * zoom
  const y = element.position.y * mmToPx * zoom

  const dimensions = useMemo(() => getElementDimensions(element), [element])
  const resizeHandles = useMemo(() => getResizeHandles(element), [element])
  const isResizable = resizeHandles.length > 0

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Exit edit mode when element is deselected
  useEffect(() => {
    if (!isSelected && isEditing) {
      handleEditComplete()
    }
  }, [isSelected])

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      if (isTextEditable && element.type === 'text') {
        setEditText(element.content)
        setIsEditing(true)
      }
    },
    [isTextEditable, element]
  )

  const handleEditComplete = useCallback(() => {
    if (isEditing && onTextEdit && editText !== '') {
      onTextEdit(editText)
    }
    setIsEditing(false)
  }, [isEditing, onTextEdit, editText])

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation()
      if (e.key === 'Enter') {
        handleEditComplete()
      } else if (e.key === 'Escape') {
        setIsEditing(false)
      }
    },
    [handleEditComplete]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Don't start drag if we're editing
      if (isEditing) {
        e.stopPropagation()
        return
      }
      e.stopPropagation()
      e.preventDefault()
      onSelect(e)
      setIsDragging(true)
      dragStartRef.current = { x: e.clientX, y: e.clientY }
      totalDragRef.current = { x: 0, y: 0 }
      setDragOffset({ x: 0, y: 0 })
    },
    [onSelect, isEditing]
  )

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: ResizeHandle) => {
      e.stopPropagation()
      e.preventDefault()
      setIsResizing(true)
      setResizeHandle(handle)

      const dims = getElementDimensions(element)
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: dims?.width || 0,
        height: dims?.height || 0,
        diameter: dims?.diameter || 0,
      }
    },
    [element]
  )

  // Drag handling
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x
      const deltaY = e.clientY - dragStartRef.current.y

      setDragOffset({ x: deltaX, y: deltaY })
      totalDragRef.current = { x: deltaX, y: deltaY }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      if (totalDragRef.current.x !== 0 || totalDragRef.current.y !== 0) {
        onDrag(totalDragRef.current.x, totalDragRef.current.y, true)
      }
      setDragOffset({ x: 0, y: 0 })
      totalDragRef.current = { x: 0, y: 0 }
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, onDrag])

  // Resize handling
  useEffect(() => {
    if (!isResizing || !resizeHandle || !onResize) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartRef.current.x
      const deltaY = e.clientY - resizeStartRef.current.y

      // Convert pixel delta to mm
      const deltaXMm = deltaX / (mmToPx * zoom)
      const deltaYMm = deltaY / (mmToPx * zoom)

      let newWidth: number | undefined
      let newHeight: number | undefined
      let newDiameter: number | undefined

      // Calculate new dimensions based on handle
      if (element.type === 'circle') {
        // Circle: uniform resize based on distance
        const delta = Math.max(Math.abs(deltaXMm), Math.abs(deltaYMm))
        const sign = (deltaXMm + deltaYMm) > 0 ? 1 : -1
        if (resizeHandle.includes('e') || resizeHandle.includes('s')) {
          newDiameter = Math.max(2, resizeStartRef.current.diameter + delta * sign)
        } else {
          newDiameter = Math.max(2, resizeStartRef.current.diameter - delta * sign)
        }
      } else {
        // Rectangle, Line, Image
        switch (resizeHandle) {
          case 'e':
            newWidth = Math.max(1, resizeStartRef.current.width + deltaXMm)
            break
          case 'w':
            newWidth = Math.max(1, resizeStartRef.current.width - deltaXMm)
            break
          case 's':
            newHeight = Math.max(1, resizeStartRef.current.height + deltaYMm)
            break
          case 'n':
            newHeight = Math.max(1, resizeStartRef.current.height - deltaYMm)
            break
          case 'se':
            newWidth = Math.max(1, resizeStartRef.current.width + deltaXMm)
            newHeight = Math.max(1, resizeStartRef.current.height + deltaYMm)
            break
          case 'sw':
            newWidth = Math.max(1, resizeStartRef.current.width - deltaXMm)
            newHeight = Math.max(1, resizeStartRef.current.height + deltaYMm)
            break
          case 'ne':
            newWidth = Math.max(1, resizeStartRef.current.width + deltaXMm)
            newHeight = Math.max(1, resizeStartRef.current.height - deltaYMm)
            break
          case 'nw':
            newWidth = Math.max(1, resizeStartRef.current.width - deltaXMm)
            newHeight = Math.max(1, resizeStartRef.current.height - deltaYMm)
            break
        }

        // For image, maintain aspect ratio when using corner handles
        if (element.type === 'image' && (newWidth !== undefined || newHeight !== undefined)) {
          const aspectRatio = resizeStartRef.current.width / resizeStartRef.current.height
          if (resizeHandle.length === 2) {
            // Corner handle - maintain aspect ratio
            if (newWidth !== undefined && newHeight === undefined) {
              newHeight = newWidth / aspectRatio
            } else if (newHeight !== undefined && newWidth === undefined) {
              newWidth = newHeight * aspectRatio
            } else if (newWidth !== undefined && newHeight !== undefined) {
              // Use the larger change
              const widthChange = Math.abs(newWidth - resizeStartRef.current.width)
              const heightChange = Math.abs(newHeight - resizeStartRef.current.height)
              if (widthChange > heightChange) {
                newHeight = newWidth / aspectRatio
              } else {
                newWidth = newHeight * aspectRatio
              }
            }
          }
        }
      }

      onResize(newWidth, newHeight, newDiameter)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      setResizeHandle(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, resizeHandle, onResize, element, mmToPx, zoom])

  // Calculate font size for inline editing input
  const getTextInputStyle = (): React.CSSProperties => {
    if (element.type !== 'text') return {}

    // Map Zebra fonts to approximate web fonts
    const fontMap: Record<string, { family: string; sizeMultiplier: number }> = {
      '0': { family: 'Arial, sans-serif', sizeMultiplier: 1 },
      'A': { family: 'monospace', sizeMultiplier: 0.6 },
      'B': { family: 'monospace', sizeMultiplier: 0.7 },
      'C': { family: 'monospace', sizeMultiplier: 0.8 },
      'D': { family: 'monospace', sizeMultiplier: 0.9 },
      'E': { family: 'Arial, sans-serif', sizeMultiplier: 0.8 },
      'F': { family: 'Arial, sans-serif', sizeMultiplier: 1.2 },
      'G': { family: 'Arial, sans-serif', sizeMultiplier: 1.5 },
      'H': { family: 'Arial, sans-serif', sizeMultiplier: 2 },
    }

    const fontConfig = fontMap[element.font] || fontMap['0']
    const dotsPerMm = label.dpi / 25.4
    const fontSizeInMm = element.fontSize / dotsPerMm
    const fontSize = fontSizeInMm * mmToPx * zoom * fontConfig.sizeMultiplier

    return {
      fontFamily: fontConfig.family,
      fontSize: `${fontSize}px`,
      lineHeight: 1,
      padding: 0,
      margin: 0,
      border: 'none',
      outline: '2px solid #3b82f6',
      background: 'rgba(255, 255, 255, 0.9)',
      color: 'black',
      minWidth: '20px',
    }
  }

  const renderElement = () => {
    // Show input field when editing text
    if (isEditing && element.type === 'text') {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleEditComplete}
          onKeyDown={handleEditKeyDown}
          style={getTextInputStyle()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      )
    }

    switch (element.type) {
      case 'text':
        return <TextElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'barcode-code128':
      case 'barcode-code39':
      case 'barcode-ean13':
      case 'barcode-ean8':
      case 'barcode-upca':
      case 'barcode-upce':
      case 'barcode-itf':
        return <BarcodeElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'gs1-code128':
        return <GS1Code128ElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'qrcode':
        return <QRCodeElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'datamatrix':
        return <DataMatrixElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'gs1-datamatrix':
        return <GS1DataMatrixElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'line':
      case 'rectangle':
      case 'circle':
        return <ShapeElementPreview element={element} zoom={zoom} mmToPx={mmToPx} />
      case 'image':
        return <ImageElementComponent element={element} zoom={zoom} mmToPx={mmToPx} />
      default:
        return null
    }
  }

  const getRotationStyle = () => {
    // Lines handle their own rotation internally (by swapping dimensions)
    // So we skip CSS rotation for lines
    if (element.type === 'line') {
      return 'rotate(0deg)'
    }

    switch (element.rotation) {
      case 'R':
        return 'rotate(90deg)'
      case 'I':
        return 'rotate(180deg)'
      case 'B':
        return 'rotate(270deg)'
      default:
        return 'rotate(0deg)'
    }
  }

  const getCursorForHandle = (handle: ResizeHandle): string => {
    switch (handle) {
      case 'n':
      case 's':
        return 'ns-resize'
      case 'e':
      case 'w':
        return 'ew-resize'
      case 'ne':
      case 'sw':
        return 'nesw-resize'
      case 'nw':
      case 'se':
        return 'nwse-resize'
      default:
        return 'pointer'
    }
  }

  const getHandlePosition = (handle: ResizeHandle): React.CSSProperties => {
    const size = 8
    const offset = -size / 2

    switch (handle) {
      case 'n':
        return { top: offset, left: '50%', transform: 'translateX(-50%)' }
      case 's':
        return { bottom: offset, left: '50%', transform: 'translateX(-50%)' }
      case 'e':
        return { right: offset, top: '50%', transform: 'translateY(-50%)' }
      case 'w':
        return { left: offset, top: '50%', transform: 'translateY(-50%)' }
      case 'ne':
        return { top: offset, right: offset }
      case 'nw':
        return { top: offset, left: offset }
      case 'se':
        return { bottom: offset, right: offset }
      case 'sw':
        return { bottom: offset, left: offset }
      default:
        return {}
    }
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={elementRef}
          className={`absolute select-none ${
            isDragging ? 'cursor-grabbing z-50' : isResizing ? 'z-50' : isEditing ? 'cursor-text z-50' : 'cursor-grab'
          } ${isSelected && !isEditing ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
          style={{
            left: x + dragOffset.x,
            top: y + dragOffset.y,
            transform: getRotationStyle(),
            transformOrigin: 'top left',
            willChange: isDragging || isResizing ? 'left, top' : 'auto',
          }}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
        >
          {renderElement()}
          {isSelected && (
            <>
              {/* Selection indicators for non-resizable elements */}
              {!isResizable && (
                <>
                  <div className="absolute -top-1 -left-1 w-2 h-2 bg-blue-500 rounded-full" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                  <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-blue-500 rounded-full" />
                  <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                </>
              )}

              {/* Resize handles */}
              {isResizable &&
                resizeHandles.map((handle) => (
                  <div
                    key={handle}
                    className="absolute w-2 h-2 bg-blue-500 border border-white rounded-sm hover:bg-blue-600 transition-colors"
                    style={{
                      ...getHandlePosition(handle),
                      cursor: getCursorForHandle(handle),
                      zIndex: 10,
                    }}
                    onMouseDown={(e) => handleResizeStart(e, handle)}
                  />
                ))}
            </>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => duplicateElement(element.id)}>
          <Copy className="h-4 w-4 mr-2" />
          Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => bringToFront(element.id)}>
          <ArrowUpToLine className="h-4 w-4 mr-2" />
          Bring to Front
        </ContextMenuItem>
        <ContextMenuItem onClick={() => bringForward(element.id)}>
          <ChevronUp className="h-4 w-4 mr-2" />
          Bring Forward
        </ContextMenuItem>
        <ContextMenuItem onClick={() => sendBackward(element.id)}>
          <ChevronDown className="h-4 w-4 mr-2" />
          Send Backward
        </ContextMenuItem>
        <ContextMenuItem onClick={() => sendToBack(element.id)}>
          <ArrowDownToLine className="h-4 w-4 mr-2" />
          Send to Back
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => deleteElement(element.id)} className="text-destructive focus:text-destructive">
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
